<template>
  <div :class="rootClass" data-testid="hero-block" :data-variant="variant">
    <div class="flex flex-col gap-6">
      <p
        v-if="eyebrow"
        class="inline-flex items-center text-xs font-semibold tracking-[0.14em] text-accent uppercase md:text-sm"
        data-testid="hero-eyebrow"
      >
        {{ eyebrow }}
      </p>
      <h1 :class="headingClass" data-testid="hero-heading">{{ heading }}</h1>
      <p v-if="lead" :class="leadClass" data-testid="hero-lead">{{ lead }}</p>

      <ul
        v-if="isLanding && bullets.length > 0"
        class="flex flex-col gap-2 text-base text-ink-strong md:text-lg"
        data-testid="hero-bullets"
      >
        <li v-for="item in bullets.slice(0, 3)" :key="item" class="flex items-start gap-3">
          <span
            class="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-soft text-accent-strong"
          >
            <BaseIcon name="check" :size="16" />
          </span>
          <span class="text-white">{{ item }}</span>
        </li>
      </ul>

      <div
        v-if="primaryCta || secondaryCta"
        class="flex flex-wrap items-center gap-3"
        data-testid="hero-ctas"
      >
        <CtaButton
          v-if="primaryCta"
          :cta="primaryCta"
          variant="primary"
          size="lg"
          testid="hero-primary-cta"
          @action="onSecondaryAction"
        >
          <template #icon-right>
            <BaseIcon name="arrow-right" :size="20" />
          </template>
        </CtaButton>
        <CtaButton
          v-if="secondaryCta && videoBuilderUrl"
          :cta="secondaryCta"
          variant="ghost"
          size="lg"
          testid="hero-secondary-cta"
          @action="onSecondaryAction"
        />
      </div>

      <StatsBlock
        v-if="isLanding && stats.length > 0"
        :items="stats"
        tone="white"
        class="mt-4 border-t border-stroke pt-6"
        data-testid="hero-stats"
      />
    </div>

    <div
      v-if="showVisualColumn"
      class="relative hidden aspect-[4/5] items-center justify-center overflow-hidden rounded-3xl bg-accent-soft lg:flex"
      data-testid="hero-visual"
    >
      <slot name="visual" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { Comment, computed, ref, useSlots } from 'vue';
import { cva } from 'class-variance-authority';
import CtaButton from '@/components/base/CtaButton.vue';
import BaseIcon from '@/components/base/BaseIcon.vue';
import StatsBlock from '@/components/domain/StatsBlock.vue';
import type { Cta } from '@/lib/cta/resolveCtaKind';
import type { HeroVariant, StatsBlockItem } from '@/types/component-types';

const rootVariants = cva('grid gap-10', {
  variants: {
    hasVisual: {
      true: 'lg:grid-cols-[minmax(0,1fr)_minmax(0,0.85fr)] lg:items-center',
      false: '',
    },
  },
  defaultVariants: { hasVisual: false },
});

const headingVariants = cva('font-extrabold tracking-tight text-ink-strong', {
  variants: {
    variant: {
      landing: 'text-4xl md:text-5xl lg:text-6xl text-white',
      compact: 'text-3xl md:text-4xl lg:text-5xl',
    },
  },
  defaultVariants: { variant: 'landing' },
});

const leadVariants = cva('text-ink-muted', {
  variants: {
    variant: {
      landing: 'text-lg md:text-xl text-white',
      compact: 'max-w-2xl text-base md:text-lg',
    },
    hasVisual: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    { variant: 'landing', hasVisual: true, class: 'max-w-2xl' },
    { variant: 'landing', hasVisual: false, class: 'max-w-3xl' },
  ],
  defaultVariants: { variant: 'landing', hasVisual: false },
});

export type { HeroVariant, HeroCta } from '@/types/component-types';

interface Props {
  variant?: HeroVariant;
  eyebrow?: string;
  heading: string;
  lead?: string;
  bullets?: readonly string[];
  primaryCta?: Cta | null;
  secondaryCta?: Cta | null;
  stats?: readonly StatsBlockItem[];
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'landing',
  eyebrow: '',
  lead: '',
  bullets: () => [],
  primaryCta: null,
  secondaryCta: null,
  stats: () => [],
});

const emit = defineEmits<{
  (e: 'secondary-action', action: Cta): void;
}>();

const videoBuilderUrl = ref(import.meta.env.VITE_VIDEO_BUILDER_URL);
const isLanding = computed(() => props.variant === 'landing');

const slots = useSlots();
const hasVisual = computed(() => {
  const vs = slots.visual?.();
  if (!vs) return false;
  return vs.some((v) => v.type !== Comment);
});

const showVisualColumn = computed(() => isLanding.value && hasVisual.value);

const rootClass = computed(() => rootVariants({ hasVisual: showVisualColumn.value }));
const headingClass = computed(() => headingVariants({ variant: props.variant }));
const leadClass = computed(() =>
  leadVariants({ variant: props.variant, hasVisual: showVisualColumn.value }),
);

function onSecondaryAction(cta: Cta): void {
  emit('secondary-action', cta);
}
</script>
